import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { searchAddresses } from '../services/geocoding';
import { useLanguage } from '../contexts/LanguageContext';
import './SimpleAddressInput.css';

export interface SimpleAddressData {
  street_address: string;
  city: string;
  postal_code: string;
  country: string;
  state?: string;
  latitude?: number;
  longitude?: number;
}

interface SimpleAddressInputProps {
  onAddressValidate: (data: SimpleAddressData) => void;
  onSwitchToAdvanced?: () => void;
  initialData?: {
    street_address?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    state?: string;
  };
  showAdvancedToggle?: boolean;
}

export interface SimpleAddressInputRef {
  validateAddress: () => Promise<boolean>;
  getFormData: () => {
    street_address: string;
    city: string;
    postal_code: string;
    country: string;
    state: string;
  };
}

const SimpleAddressInput = forwardRef<SimpleAddressInputRef, SimpleAddressInputProps>(({
  onAddressValidate,
  onSwitchToAdvanced,
  initialData,
  showAdvancedToggle = true,
}, ref) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    street_address: initialData?.street_address || '',
    city: initialData?.city || '',
    postal_code: initialData?.postal_code || '',
    country: initialData?.country || '',
    state: initialData?.state || '',
  });
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear validation error when user types
    if (validationError) {
      setValidationError('');
    }
  };

  /**
   * Validate address by geocoding it
   * Called by parent component (e.g., on form submit)
   */
  const validateAddress = async (): Promise<boolean> => {
    setIsValidating(true);
    setValidationError('');

    try {
      // Build full address string
      const addressParts = [
        formData.street_address,
        formData.city,
        formData.postal_code,
        formData.country,
      ].filter(Boolean);

      if (addressParts.length < 3) {
        setValidationError('Veuillez remplir au moins la rue, la ville et le pays');
        setIsValidating(false);
        return false;
      }

      const fullAddress = addressParts.join(', ');

      // Geocode the address using high-quality mode
      const results = await searchAddresses(fullAddress, 1, undefined, undefined, 'high-quality');

      if (results.length === 0) {
        setValidationError(
          'Impossible de localiser cette adresse. Vérifiez votre saisie ou utilisez le mode avancé.'
        );
        setIsValidating(false);
        return false;
      }

      const geocoded = results[0];

      // Prepare data with both user input (for storage) and geocoded coordinates
      const validatedData = {
        street_address: formData.street_address,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country,
        state: formData.state || geocoded.state,
        latitude: geocoded.latitude,
        longitude: geocoded.longitude,
      };

      // Call parent callback with validated data
      onAddressValidate(validatedData);
      setIsValidating(false);
      return true;
    } catch (error) {
      console.error('Address validation error:', error);
      setValidationError(
        'Erreur lors de la validation de l\'adresse. Veuillez réessayer ou utiliser le mode avancé.'
      );
      setIsValidating(false);
      return false;
    }
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    validateAddress,
    getFormData: () => formData,
  }));

  return (
    <div className="simple-address-input">
      <div className="form-group">
        <label htmlFor="street_address">
          {t('auth.register.streetAddress')} <span className="required">*</span>
        </label>
        <input
          type="text"
          id="street_address"
          name="street_address"
          value={formData.street_address}
          onChange={handleChange}
          required
          autoComplete="street-address"
          placeholder="23 rue de la Paix"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="city">
            {t('auth.register.city')} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            autoComplete="address-level2"
            placeholder="Paris"
          />
        </div>

        <div className="form-group">
          <label htmlFor="postal_code">
            {t('auth.register.postalCode')}
          </label>
          <input
            type="text"
            id="postal_code"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            autoComplete="postal-code"
            placeholder="75001"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="state">{t('auth.register.state')}</label>
          <input
            type="text"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
            autoComplete="address-level1"
            placeholder="Île-de-France"
          />
        </div>

        <div className="form-group">
          <label htmlFor="country">
            {t('auth.register.country')} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            required
            autoComplete="country-name"
            placeholder="France"
          />
        </div>
      </div>

      {validationError && (
        <div className="validation-error" style={{ marginTop: '10px' }}>
          {validationError}
        </div>
      )}

      {isValidating && (
        <div className="validating-message" style={{ marginTop: '10px', color: 'var(--text-secondary)' }}>
          ⏳ Validation de l'adresse en cours...
        </div>
      )}

      {showAdvancedToggle && onSwitchToAdvanced && (
        <div className="address-mode-toggle">
          <p className="toggle-hint">
            ⚠️ Ces champs ne sont pas adaptés à votre adresse ?
          </p>
          <button
            type="button"
            className="btn-toggle-advanced"
            onClick={onSwitchToAdvanced}
          >
            → Utiliser le mode avancé (carte interactive)
          </button>
        </div>
      )}
    </div>
  );
});

SimpleAddressInput.displayName = 'SimpleAddressInput';

export default SimpleAddressInput;
